import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../components/AppLayout';
import { MaterialService } from '../services/materialService';
import { Material, Category } from '../types/index';

export const AdminMaterialsPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [materials, setMaterials] = useState<Material[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    materialName: '',
    thickness: '',
    size: '',
    color: '',
    weight: '',
    basePrice: '',
    markupFactor: '2.0',
  });


  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [materialsData, categoriesData] = await Promise.all([
        MaterialService.getAllMaterials(),
        MaterialService.getCategories(),
      ]);
      setMaterials(materialsData);
      setCategories(categoriesData);
      if (categoriesData.length > 0) {
        setFormData((prev) => ({ ...prev, categoryId: categoriesData[0].id }));
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const updated = {
      ...formData,
      [name]: value,
    };

    // Auto-generate structured name
    if (name === 'categoryId' || name === 'materialName' || name === 'color' || name === 'thickness' || name === 'size') {
      updated.name = generateMaterialName(updated);
    }

    setFormData(updated);
  };

  const generateMaterialName = (data: typeof formData): string => {
    const categoryName = categories.find(c => c.id === data.categoryId)?.name || '';
    const parts = [categoryName, data.materialName, data.color, data.thickness, data.size].filter(Boolean);
    return parts.join(' - ');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);

      const materialData = {
        ...formData,
        weight: parseFloat(formData.weight) || 0,
        basePrice: parseFloat(formData.basePrice) || 0,
        markupFactor: parseFloat(formData.markupFactor) || 2.0,
      };

      if (editingId) {
        await MaterialService.updateMaterial(editingId, materialData as any);
      } else {
        await MaterialService.createMaterial(materialData as any);
      }

      resetForm();
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = (material: Material) => {
    setFormData({
      name: material.name,
      categoryId: material.categoryId,
      materialName: '',
      thickness: String(material.thickness),
      size: material.size,
      color: material.color,
      weight: String(material.weight),
      basePrice: String(material.basePrice),
      markupFactor: String(material.markupFactor),
    });
    setEditingId(material.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this material?')) return;
    try {
      setError(null);
      await MaterialService.deleteMaterial(id);
      await loadData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      categoryId: categories[0]?.id || '',
      materialName: '',
      thickness: '',
      size: '',
      color: '',
      weight: '',
      basePrice: '',
      markupFactor: '2.0',
    });
    setEditingId(null);
    setShowForm(false);
  };

  const toggleCategory = (categoryId: string) => {
    const newSet = new Set(expandedCategories);
    if (newSet.has(categoryId)) {
      newSet.delete(categoryId);
    } else {
      newSet.add(categoryId);
    }
    setExpandedCategories(newSet);
  };

  if (loading) {
    return (
      <AppLayout>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <p>Loading materials...</p>
        </div>
      </AppLayout>
    );
  }

  const materialsByCategory = categories.map((category) => ({
    category,
    materials: materials.filter((m) => m.categoryId === category.id),
  })).filter((group) => group.materials.length > 0);

  return (
    <AppLayout>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1>Manage Materials</h1>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            style={{
              padding: '10px 16px',
              background: 'var(--primary-button)',
              color: 'var(--primary-button-text)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
            }}
          >
            + Add Material
          </button>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', marginBottom: '20px', borderRadius: '6px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
            {error}
          </div>
        )}

        {showForm && (
          <div style={{ background: 'var(--card-background)', padding: '24px', marginBottom: '24px', borderRadius: '12px', border: '1px solid var(--border-color)' }}>
            <h2 style={{ margin: '0 0 20px 0', color: 'var(--main-text)', fontSize: '18px', fontWeight: '600' }}>
              {editingId ? 'Edit Material' : 'Add Material'}
            </h2>
            <form onSubmit={handleSubmit}>
              {/* Primary Fields Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Category *</label>
                  <select
                    name="categoryId"
                    value={formData.categoryId}
                    onChange={handleChange}
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Material Name (e.g., MDF, Plywood) *</label>
                  <input
                    type="text"
                    name="materialName"
                    value={formData.materialName}
                    onChange={handleChange}
                    placeholder="e.g., MDF, Plywood"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Color (optional)</label>
                  <input
                    type="text"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                    placeholder="e.g., Natural, Black, White"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Secondary Fields Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '16px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Thickness (optional)</label>
                  <input
                    type="text"
                    name="thickness"
                    value={formData.thickness}
                    onChange={handleChange}
                    placeholder="e.g., 1/4 inch, 3/4 inch"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Sheet Size (optional)</label>
                  <select
                    name="size"
                    value={formData.size}
                    onChange={handleChange}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  >
                    <option value="">Select a size or enter custom</option>
                    <option value="4x8">4x8</option>
                    <option value="4x10">4x10</option>
                    <option value="5x8">5x8</option>
                    <option value="5x10">5x10</option>
                    <option value="5x12">5x12</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Weight (lbs)</label>
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    placeholder="0"
                    step="0.01"
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>
              </div>

              {/* Pricing Fields Row */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Base Price ($) *</label>
                  <input
                    type="number"
                    name="basePrice"
                    value={formData.basePrice}
                    onChange={handleChange}
                    placeholder="0.00"
                    step="0.01"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '6px', color: 'var(--main-text)', fontWeight: '600', fontSize: '14px' }}>Markup Factor *</label>
                  <input
                    type="number"
                    name="markupFactor"
                    value={formData.markupFactor}
                    onChange={handleChange}
                    placeholder="2.0"
                    step="0.1"
                    required
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', backgroundColor: 'var(--main-background)', color: 'var(--main-text)', boxSizing: 'border-box', fontSize: '14px' }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" style={{ padding: '10px 16px', background: 'var(--primary-button)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>
                  {editingId ? 'Update' : 'Create'} Material
                </button>
                <button type="button" onClick={resetForm} style={{ padding: '10px 16px', background: 'var(--page-background)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', color: 'var(--main-text)', fontWeight: '500' }}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {materials.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 20px' }}>
            No materials yet. Create your first material!
          </p>
        ) : (
          <div>
            {materialsByCategory.map((group) => (
              <div key={group.category.id} style={{ marginBottom: '16px' }}>
                <button
                  onClick={() => toggleCategory(group.category.id)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'var(--primary-button)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '16px',
                  }}
                >
                  <span>{group.category.name} ({group.materials.length})</span>
                  <span>{expandedCategories.has(group.category.id) ? '▼' : '▶'}</span>
                </button>

                {expandedCategories.has(group.category.id) && (
                  <div style={{ background: 'var(--card-background)', borderRadius: '0 0 8px 8px', border: '1px solid var(--border-color)', borderTop: 'none', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ background: 'var(--page-background)', borderBottom: '1px solid var(--border-color)' }}>
                        <tr>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Name</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Specs</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Weight</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Base Price</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Markup</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Marked Price</th>
                          <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600', color: 'var(--main-text)' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.materials.map((material) => (
                          <tr key={material.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                            <td style={{ padding: '12px 16px', color: 'var(--main-text)' }}>{material.name}</td>
                            <td style={{ padding: '12px 16px', color: 'var(--secondary-text)' }}>
                              {[material.color, material.thickness, material.size].filter(Boolean).join(', ') || '—'}
                            </td>
                            <td style={{ padding: '12px 16px', color: 'var(--secondary-text)' }}>
                              {material.weight > 0 ? `${material.weight} lbs` : '—'}
                            </td>
                            <td style={{ padding: '12px 16px', color: 'var(--main-text)' }}>
                              ${material.basePrice.toFixed(2)}
                            </td>
                            <td style={{ padding: '12px 16px', color: 'var(--secondary-text)' }}>
                              {material.markupFactor}x ({((material.markupFactor - 1) * 100).toFixed(0)}%)
                            </td>
                            <td style={{ padding: '12px 16px', fontWeight: '600', color: 'var(--primary-button)' }}>
                              ${(material.basePrice * material.markupFactor).toFixed(2)}
                            </td>
                            <td style={{ padding: '12px 16px' }}>
                              <button onClick={() => handleEdit(material)} style={{ padding: '6px 12px', marginRight: '8px', background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary-button)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                                Edit
                              </button>
                              <button onClick={() => handleDelete(material.id)} style={{ padding: '6px 12px', background: 'rgba(239, 68, 68, 0.1)', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}>
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
};
